<script setup lang="ts">
// Have to explicitly import the types due to `defineProps` macro compilation limitation
import { InputProps, getAriaIDs } from './inputHelper'

const props = defineProps<InputProps>()
const inputElement = ref<HTMLInputElement | null>(null)
const ariaIds = getAriaIDs(props)
const model = defineModel()
const isEmpty = computed(() => model.value === '')
defineExpose({
  focus() {
    inputElement.value?.focus()
  },
})
</script>

<template>
  <div class="input--text" :class="{ 'input--not-empty': !isEmpty, 'input--error': error }">
    <label>
      <span>{{ label }}{{ required ? ' *' : '' }}</span>
      <input
        ref="inputElement"
        v-model="model"
        placeholder=" "
        v-bind="$attrs"
        :name
        :required="required"
        :readonly="readonly"
        :tabindex="readonly ? -1 : undefined"
        :data-testid="inputTestId"
        :aria-describedby="ariaIds"
        :aria-invalid="!!error"
        :class="{ 'input--error-highlight': error }"
      />
      <slot name="input-action" />
    </label>
    <InputMessage :name="name" :error="error" :hint="hint" :attempt="attempt" />
  </div>
</template>

<style lang="postcss">
.input--text {
  label {
    width: 100%;
    display: block;
    position: relative;

    span {
      position: absolute;
      left: 1.05rem;
      top: 1rem;
      color: var(--text-tertiary);
      transition: var(--transition-0);
      pointer-events: none;
    }
  }

  input {
    appearance: none;
    border-radius: 0;
    background-color: transparent;
    border: 1px solid var(--border-input);
    min-height: 3.125rem;
    padding: var(--space-30) var(--space-40);
    outline-offset: 0.2rem;
    width: 100%;
    display: inline-block; /* required for scale */
    padding: 1rem 0.5rem 0.2rem 1rem;
    transition: var(--transition-0);
    border-color: var(--border-input);
    outline: none;

    @media (--mobile-only) {
      font-size: var(--space-50); /* Avoid iOS page zoom - min 16px font-size required on Inputs */
    }

    &:is(:-webkit-autofill, :autofill) {
      background: transparent;
      -webkit-background-clip: text;
      background-clip: text;
    }

    &:focus {
      border: 1px solid var(--border-dark-10);
    }
    &:focus-visible {
      border: 2px solid var(--status-info);
    }
  }

  &:has(input:focus) label span,
  &.input--not-empty label span,
  &:has(input:not(:placeholder-shown)) label span {
    top: 0.4rem;
    font-size: var(--text-small);
  }

  .input--error-highlight {
    border-color: var(--status-error);
  }
}
</style>
